import { type ReactNode, useEffect } from 'react';
import { I18nextProvider } from 'react-i18next';
import i18n, { LOCALE_STORAGE_KEY } from './i18n.js';
import { RTL_LOCALES } from './locales.js';

export interface I18nProviderProps {
  children: ReactNode;
  /** Called after every locale change so the app can sync its own state (e.g., uiStore). */
  onLocaleChange?: (locale: string, isRtl: boolean) => void;
}

export function applyLocale(
  locale: string,
  onLocaleChange?: (locale: string, isRtl: boolean) => void,
): void {
  console.log(
    '[i18n] applyLocale called →',
    locale,
    '| current i18n.language:',
    i18n.language,
    '| i18n.isInitialized:',
    i18n.isInitialized,
  );
  const isRtl = RTL_LOCALES.includes(locale);
  document.documentElement.lang = locale;
  document.documentElement.dir = isRtl ? 'rtl' : 'ltr';
  localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  console.log('[i18n] localStorage[ar:locale] set to:', locale);
  i18n
    .changeLanguage(locale)
    .then(() =>
      console.log('[i18n] changeLanguage resolved → i18n.language is now:', i18n.language),
    )
    .catch((err) => console.error('[i18n] changeLanguage rejected:', err));
  onLocaleChange?.(locale, isRtl);
}

export function I18nProvider({ children, onLocaleChange }: I18nProviderProps) {
  useEffect(() => {
    // Apply direction on mount for the detected/stored locale
    const lang = i18n.language || 'en';
    const isRtl = RTL_LOCALES.includes(lang);
    document.documentElement.lang = lang;
    document.documentElement.dir = isRtl ? 'rtl' : 'ltr';
  }, []);

  useEffect(() => {
    const handler = (lng: string) => {
      console.log('[i18n] I18nProvider languageChanged handler →', lng);
      const isRtl = RTL_LOCALES.includes(lng);
      document.documentElement.lang = lng;
      document.documentElement.dir = isRtl ? 'rtl' : 'ltr';
      onLocaleChange?.(lng, isRtl);
      console.log('[i18n] onLocaleChange callback fired with:', lng, '| isRtl:', isRtl);
    };
    i18n.on('languageChanged', handler);
    return () => i18n.off('languageChanged', handler);
  }, [onLocaleChange]);

  return <I18nextProvider i18n={i18n}>{children}</I18nextProvider>;
}
